/**
 * How long a recording runs, even when its file does not say.
 *
 * A recording made in the browser (MediaRecorder's WebM) carries no duration:
 * the element reports `Infinity` until it has read to the end. A player bar
 * that falls back on "as far as it has played" then rescales on every tick,
 * and its handle runs back and forth. Asking it to seek past the end makes the
 * browser read the whole file and settle the real figure; it is put back at
 * the start before anything is heard.
 *
 * Resolves to the duration in seconds, or `null` if the browser never tells
 * within `timeoutMs` — the caller then shows what it already knew.
 */
export function resolveMediaDuration(
    media: HTMLMediaElement,
    timeoutMs = 4000
): Promise<number | null> {
    const known = () => (Number.isFinite(media.duration) && media.duration > 0 ? media.duration : null);

    return new Promise((resolve) => {
        let settled = false;
        const finish = (value: number | null) => {
            if (settled) return;
            settled = true;
            media.removeEventListener("loadedmetadata", onMetadata);
            media.removeEventListener("durationchange", onDuration);
            clearTimeout(timer);
            resolve(value);
        };
        const onDuration = () => {
            const value = known();
            if (value === null) return;
            media.currentTime = 0;
            finish(value);
        };
        const onMetadata = () => {
            const value = known();
            if (value !== null) {
                finish(value);
                return;
            }
            media.addEventListener("durationchange", onDuration);
            media.currentTime = Number.MAX_SAFE_INTEGER;
        };
        const timer = setTimeout(() => finish(known()), timeoutMs);

        if (media.readyState >= 1) onMetadata();
        else media.addEventListener("loadedmetadata", onMetadata);
    });
}
